import React, { useState, useContext, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { useWindowSize } from 'react-use'
import { AccountContext } from '@/contexts/account'
import { wholeContainerStyle, mainColumnStyle, mainContainerStyle, tableHeaderThStyle } from '@/constants/layout'
import { fetchCustomerHistory, updateAdminCheckinList } from '@/apis/aipass'
import { setHasRepeatPlugin } from '@/libs/plugins'
import { LoadingFull } from '@/components/molecules/loading-full'
import { Header } from '@/components/organisms/header'
import { SideMenu } from '@/components/organisms/side-menu'
import { itemWrapperStyle, PersonInfo } from '@/components/organisms/customer/_detail/person-info'
import { Button } from '@/components/atoms/button'
import { EditCustomerInfoModal } from '@/components/organisms/customer/_detail/edit-customer-info-modal'
import { CustomerList } from '@/components/pages/customer-manager/_detail/customer-list'
import { CustomerReservationType, CustomerType } from '@/models/customer-manager/customer'
import { CustomerTagInput } from '@/components/molecules/customer/customer-tag-input'

export const CustomerDetail: React.FC = () => {
  const windows = useWindowSize()
  const { t } = useTranslation()
  const { plugins, account } = useContext<any>(AccountContext)
  const history = useHistory()
  const params = useParams<any>()
  const { id } = params
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [customer, setCustomer] = useState<CustomerType>()
  const [isOpenEditCustomerInfoModal, setIsOpenEditCustomerInfoModal] = useState<boolean>(false)
  const [reservations, setReservations] = useState<CustomerReservationType[]>([])
  const [salesInfo, setSalesInfo] = useState<{ totalSalesPrice: number; averageSalesPrice: number }>()
  const [isEditTag, setIsEditTag] = useState<boolean>(false)
  const [tags, setTags] = useState<string[]>([])
  const hasRepeatPlugin = setHasRepeatPlugin(plugins)

  const _fetchCustomerHistory = async () => {
    setIsLoading(true)
    await fetchCustomerHistory(id)
      .then(res => {
        setCustomer({
          ...res.personalInfo,
          id,
          tagInfo: res.tagInfo,
          stayCount: res.stayCount,
          totalSalesPrice: res.totalSalesPrice,
          averageSalesPrice: res.averageSalesPrice,
        })
        if (!res?.reservations?.length) return
        setSalesInfo({ totalSalesPrice: res.totalSalesPrice, averageSalesPrice: res.averageSalesPrice })
        setReservations(res.reservations)
      })
      .catch(() => {
        console.log(t('Communication failed'))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const onClickCancelTag = () => {
    setIsEditTag(false)
    setTags(customer?.tagInfo?.tags?.map(tagInfo => tagInfo.id) || [])
  }

  // Tag update
  const onClickSaveTag = async () => {
    if (!customer) {
      return
    }

    setIsLoading(true)
  }

  const goBack = () => {
    history.push({ pathname: '/customer/list', search: window.location.search })
  }

  useEffect(() => {
    if (account && plugins) {
      _fetchCustomerHistory()
    }
  }, [account, plugins])

  useEffect(() => {
    if (customer) {
      updateAdminCheckinList({ id: customer.id, mode: 'customerRead' }).catch(() => {
        console.log(t('Communication failed'))
      })
      setTags(customer?.tagInfo?.tags?.map(tagInfo => tagInfo.id) || [])
    }
  }, [customer])

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div css={mainColumnStyle}>
        <Header title={t('Customer management')} goBack={goBack} isBack />
        <div css={mainContainerStyle}>
          <div css={containerWrapperStyle}>
            <div css={leftContainerStyle}>
              <PersonInfo
                customer={customer}
                reservations={reservations}
                onClickEditCustomerInfo={() => setIsOpenEditCustomerInfoModal(true)}
              />
            </div>
            <div css={rightContainerStyle}>
              <div css={itemWrapperStyle}>
                <img src={require('@/static/images/stay.svg')} />
                {hasRepeatPlugin ? (
                  <p>
                    {t('Number of nights stayed')} <span>{customer?.stayCount}</span>
                  </p>
                ) : (
                  <p>-</p>
                )}
              </div>
              <div css={itemWrapperStyle}>
                <div css={inputWrapperStyle}>{customer && <CustomerTagInput customerId={customer.id} customerTags={tags} />}</div>
              </div>
              {isEditTag && (
                <div css={editButtonWrapperStyle}>
                  <Button width={100} height={32} buttonType={3} fontSize={12} marginRight={16} onClick={onClickCancelTag}>
                    {t('Cancel')}
                  </Button>
                  <Button width={58} height={32} buttonType={1} fontSize={12} onClick={onClickSaveTag}>
                    {t('Save')}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div css={[listContainerStyle]}>
            <div css={listHeaderStyle}>
              <div css={listTitleStyle}>{t('Accommodation history')}</div>

              <div style={{ display: 'flex', gap: 24, fontSize: 14, paddingRight: 32, fontWeight: 'bold' }}>
                <p>{`${t('Total amount used')} : ${
                  salesInfo?.totalSalesPrice ? `¥${salesInfo.totalSalesPrice.toLocaleString()}` : '-'
                }`}</p>
                <p>{`${t('Average amount used')} : ${
                  salesInfo?.averageSalesPrice ? `¥${salesInfo.averageSalesPrice.toLocaleString()}` : '-'
                }`}</p>
              </div>
            </div>
            <div
              css={{
                overflow: 'auto',
                height: windows.height - 350,
                backgroundColor: '#fff',
                borderBottomLeftRadius: 5,
                borderBottomRightRadius: 5,
              }}
            >
              <table css={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th css={[tableHeaderThStyle, { paddingLeft: 32, width: 200 }]}>{t('Date of stay')}</th>
                    <th css={[tableHeaderThStyle, { width: 110 }]}>{t('Name of facility')}</th>
                    <th css={[tableHeaderThStyle, { width: 48 }]}>{t('Number of people')}</th>
                    <th css={[tableHeaderThStyle, { width: 200 }]}>{t('Plan name')}</th>
                    <th css={[tableHeaderThStyle, { width: 222 }]}>{t('Room type / Number of rooms')}</th>
                    <th css={[tableHeaderThStyle, { width: 94 }]}>{t('amount used')}</th>
                    <th css={[tableHeaderThStyle, { width: 24 }]}>{t('Evaluation')}</th>
                    <th css={[tableHeaderThStyle, { width: 340 }]}>{t('Memo')}</th>
                  </tr>
                </thead>
                <tbody css={{ overflowY: 'auto' }}>
                  {reservations &&
                    reservations?.map((reservation, index) => (
                      <CustomerList key={reservation.accommodationId} reservation={reservation} index={index} />
                    ))}
                </tbody>
              </table>
            </div>
          </div>
          {isOpenEditCustomerInfoModal && customer && (
            <EditCustomerInfoModal
              customer={customer}
              onCancel={() => setIsOpenEditCustomerInfoModal(false)}
              onSave={() => {
                setIsOpenEditCustomerInfoModal(false)
                _fetchCustomerHistory()
              }}
            />
          )}
        </div>
      </div>
      <LoadingFull isLoading={isLoading} />
    </div>
  )
}

const containerWrapperStyle = css({
  display: 'flex',
})

const sectionStyle = css({
  width: '30%',
  background: '#FFFFFF',
  boxShadow: '0px 0px 6px #0000001A',
  display: 'flex',
  borderRadius: 5,
  padding: '24px 16px 24px 32px',
})

const leftContainerStyle = css(sectionStyle, {
  marginRight: 16,
  width: '70%',
  justifyContent: 'space-between',
  overflow: 'hidden',
})

const rightContainerStyle = css(sectionStyle, {
  width: '30%',
  flexDirection: 'column',
  backgroundColor: '#FFF',
  justifyContent: 'center',
})

const listContainerStyle = css({
  marginTop: 16,
  boxShadow: '0px 0px 6px #0000001A',
})

const listHeaderStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '16px 32px',
  height: 50,
  alignItems: 'center',
  backgroundColor: '#FFF',
  borderTopLeftRadius: 5,
  borderTopRightRadius: 5,
})

const listTitleStyle = css({
  color: '#272727',
  fontSize: 18,
  fontWeight: 'bold',
  padding: '8px 0',
})

const inputWrapperStyle = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
})

const editButtonWrapperStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
})
